// Constantes del "Paquete de Contexto del Cliente" (checklist "armá el contexto de este cliente").
// Espeja el catálogo CHECKLIST del backend (core/services/context_pack.py). El backend
// devuelve `kind` / `priority` / `destination` / `present` / `matched_documents`; el COPY (label,
// desc) vive acá en i18n bajo client_memory.context_pack.items.<KIND>.*.

export const PRIORITY = {CORE: 'CORE', SECONDARY: 'SECONDARY'};

export const DESTINATION = {
    MEMORY: 'MEMORY',
    MEDIA_PLAN: 'MEDIA_PLAN',
    DO_NOT_UPLOAD: 'DO_NOT_UPLOAD',
};

// Tipos que la agencia puede DECLARAR al subir material desde un ítem del checklist. Espeja
// `_DECLARABLE_KINDS_ON_CREATE` del backend: son los kinds cuyo destino es la Memoria, menos
// MEETING_TRANSCRIPT (ese lo fija el `source=MEETING` de la vía "Cargar reunión", no una
// declaración). Se usa para validar el `?kind=` de la URL antes de fijarlo en el formulario.
// PERFORMANCE_REPORT es declarable y re-etiquetable pero NO es un ítem del checklist: el Paquete
// de Contexto lista lo que conviene cargar una vez, y un informe de resultados es material
// recurrente. Existe como tipo para que un informe con datos que Dashbo no tiene (CRM, ventas
// offline, embudo interno) entre como documento legible y se guarde sin extraerle hechos.
export const DECLARABLE_KINDS = [
    'KICKOFF_BRIEF', 'BUYER_PERSONA', 'MARKETING_PLAN',
    'SCOPE_AGREEMENT', 'BRAND_GUIDELINES', 'AUDIENCE_RESEARCH', 'PRODUCT_FAQ',
    'PERFORMANCE_REPORT', 'OTHER',
];
export const isDeclarableKind = (kind) => DECLARABLE_KINDS.includes(kind);

// Al ELEGIR qué se va a subir, «Otro material» es la opción PRINCIPAL y los tipos específicos
// quedan abajo como sugerencias. Estaba al revés —los ocho en una lista, con «Otro material»
// último— y en el piloto la conclusión fue que no se podían subir documentos: si ninguno de los
// siete nombres describe lo que tenés en la mano, la salida estaba escondida al final (card
// JH1GK3r9). Declarar el tipo sigue valiendo, pero es opcional: le da contexto previo a la IA.
//
// Es un orden de PRESENTACIÓN, no otro conjunto: `DECLARABLE_KINDS` sigue siendo la lista que
// valida el `?kind=` de la URL y la que arma los tipos del re-etiquetado.
export const PICK_PRIMARY_KIND = 'OTHER';
export const PICK_SPECIFIC_KINDS = DECLARABLE_KINDS.filter((kind) => kind !== PICK_PRIMARY_KIND);

// Tipos entre los que se puede RE-ETIQUETAR un documento ya cargado. Suma MEETING_TRANSCRIPT:
// corregir "esto en realidad es una transcripción" es válido y el backend acomoda el origen.
export const RELABEL_KINDS = ['MEETING_TRANSCRIPT', ...DECLARABLE_KINDS];

// Clave i18n del label/desc de un ítem del checklist (namespace client_memory.context_pack.items.*).
export const kindLabelKey = (kind) => `client_memory.context_pack.items.${kind}.label`;
export const kindDescKey = (kind) => `client_memory.context_pack.items.${kind}.desc`;

// Ícono (FontAwesome solid, vía `<i className="fas fa-...">`) por tipo de documento — le da
// escaneabilidad a la lista (cada material se reconoce de un vistazo) en vez de puntos iguales.
// Los DO_NOT_UPLOAD usan `ban` (no es su tipo, es la señal "no subir esto"). Fallback: file-alt.
// OJO: el webfont cargado es FontAwesome 5.2.0 (App.scss) — usar SOLO nombres FA5 (los de FA6+
// como `table-cells`/`magnifying-glass-chart` renderizan VACÍO). Verificado en prod-local.
const KIND_ICON = {
    MEETING_TRANSCRIPT: 'comments',
    KICKOFF_BRIEF: 'clipboard-list',
    BUYER_PERSONA: 'user-tag',
    MARKETING_PLAN: 'bullseye',
    MEDIA_PLAN: 'table',
    SCOPE_AGREEMENT: 'file-signature',
    BRAND_GUIDELINES: 'palette',
    AUDIENCE_RESEARCH: 'chart-pie',
    PRODUCT_FAQ: 'tags',
    PERFORMANCE_REPORT: 'chart-line',
    METRICS_REPORTS: 'ban',
    BILLING_SHEETS: 'ban',
};
export const kindIcon = (kind) => KIND_ICON[kind] || 'file-alt';

// CTA de un ítem faltante, por (destination, kind). Devuelve el path relativo a /client/:id
// o null si el ítem no tiene CTA (los DO_NOT_UPLOAD son educativos, sin acción).
//   - MEMORY: va a Cargar con el tipo YA FIJADO (`?kind=`). Que el usuario declare el tipo desde el
//     ítem que clickeó es más confiable que adivinarlo después: el checklist marca presente lo que
//     acaba de subir. Antes esto abría el modal de Drive y el tipo lo decidía el clasificador, así
//     que un brief podía entrar como "otro" y el ítem seguía figurando pendiente.
//   - La reunión no lleva `kind`: su tipo lo define el `source=MEETING` del formulario.
//   - MEDIA_PLAN: la pantalla de media plan del cliente.
//   - DO_NOT_UPLOAD: sin CTA.
export function ctaPathForItem(item, clientId) {
    if (item.destination === DESTINATION.MEDIA_PLAN) {
        return `/client/${clientId}/media-plan`;
    }
    if (item.destination === DESTINATION.MEMORY) {
        if (item.kind === 'MEETING_TRANSCRIPT') {
            return `/client/${clientId}/memory/setup`;
        }
        return `/client/${clientId}/memory/setup?kind=${item.kind}`;
    }
    return null;
}

// ¿Este ítem faltante se pide al cliente (modal "Pedir material")? Solo lo que va a Memoria:
// el media plan es una planilla que arma la agencia, y los DO_NOT_UPLOAD no se piden.
export const isRequestableFromClient = (item) => item.destination === DESTINATION.MEMORY;

export const isMediaPlanItem = (item) => item.destination === DESTINATION.MEDIA_PLAN;

// Resumen del Paquete de Contexto: la DEFINICIÓN ÚNICA que leen las dos superficies (el checklist
// de la ficha/panel y la guía del acto Alimenta del Recorrido). Es pura — se testea sin React.
//
// Dos decisiones que importan:
//
// 1. **Los contadores se derivan de los `items`, NO de `pack.core_present`/`core_total`.** El
//    backend ya no manda el ítem del media plan a las agencias sin el módulo, pero mientras ese
//    deploy no salga (o si el usuario está viendo una respuesta cacheada) el filtro de acá sostiene
//    la misma promesa: un checklist que se puede terminar. Cuando el backend deja de mandarlo, este
//    filtro no encuentra nada que sacar y el resultado es idéntico — no hay que tocar una línea.
// 2. **`present` manda sobre la posición.** El progreso se pintaba por índice (`i < corePresent`),
//    así que con el 4º ítem presente y los 3 primeros faltando, la barra marcaba los 3 primeros y
//    contradecía a la lista que estaba justo debajo.
//
// `mediaPlanAvailable` puede venir `null`/`undefined`: significa "todavía no sé" (los módulos del
// usuario no cargaron) y entonces NO se filtra nada. Arrancar en `false` le sacaría el ítem por
// medio segundo a una agencia que sí lo tiene, moviendo el contador bajo el ojo del usuario.
export function summarizeContextPack(rawItems, {mediaPlanAvailable} = {}) {
    const all = Array.isArray(rawItems) ? rawItems : [];
    const items = mediaPlanAvailable === false ? all.filter((i) => !isMediaPlanItem(i)) : all;

    const core = items.filter((i) => i.priority === PRIORITY.CORE);
    // Secundarios REALES (material que sí se sube): los educativos DO_NOT_UPLOAD también vienen con
    // priority SECONDARY del catálogo, pero tienen su propia sección — acá se excluyen (si no,
    // aparecen duplicados y "más material que suma" contradice su propio "no hace falta").
    const secondary = items.filter((i) => i.priority === PRIORITY.SECONDARY
        && i.destination !== DESTINATION.DO_NOT_UPLOAD);
    const doNotUpload = items.filter((i) => i.destination === DESTINATION.DO_NOT_UPLOAD);
    const corePresent = core.filter((i) => i.present).length;

    return {
        items,
        core,
        secondary,
        doNotUpload,
        corePresent,
        coreTotal: core.length,
        missingCore: core.filter((i) => !i.present),
        // Faltantes que se le pueden pedir al cliente (destino MEMORY), CORE o secundarios.
        requestableMissing: items.filter((i) => !i.present && isRequestableFromClient(i)),
        isComplete: core.length > 0 && corePresent >= core.length,
    };
}
